import {
  addLeadingZeroesToZip,
  downcaseEmail,
  standardizeFullName,
  validateCheckInBeforeCheckOut,
  validateMonthlyBillingRequiresMonthlyPricing,
  validateNonZeroDimensionForDimensionalPricing,
} from "../hooks"
import {
  addressFields,
  contactFields,
  getBoatFields,
  getRateFields,
  toPricingStructureSelectOptions,
} from "./shared"

const toSelectOptions = (items) =>
  items.map(({ id, name }) => ({ value: id, label: name }))

const toBillingScheduleSelectOptions = (items) =>
  items.map((value) => ({ value, label: value }))

export const getReservationImportConfig = ({
  spaces,
  pricingStructures,
  billingSchedules,
  requestedSpaceTypes,
  boatTypes,
  itemizedTaxEnabled,
  offlinePaymentTypes,
}) => {
  const spaceSelectOptions = toSelectOptions(spaces)
  const pricingStructureSelectOptions =
    toPricingStructureSelectOptions(pricingStructures)
  const billingScheduleSelectOptions =
    toBillingScheduleSelectOptions(billingSchedules)

  const baseBoatFields = getBoatFields(boatTypes)
  const boatFieldsWithOverrides = {
    ...baseBoatFields,
    boatName: {
      ...baseBoatFields.boatName,
      validators: [{ validate: "required" }],
    },
    measurementUnit: {
      ...baseBoatFields.measurementUnit,
      validators: [{ validate: "required" }],
    },
    lengthOverall: {
      ...baseBoatFields.lengthOverall,
      validators: [{ validate: "required" }],
    },
  }

  const rateFields = getRateFields(pricingStructureSelectOptions, {
    includeTax: !itemizedTaxEnabled,
  })

  const fields = [
    ...Object.values(contactFields),
    ...Object.values(addressFields),
    ...Object.values(boatFieldsWithOverrides),
    {
      label: "Requested Space Type",
      key: "requested_space_type",
      type: "select",
      selectOptions: requestedSpaceTypes,
      validators: [{ validate: "required" }],
    },
    {
      label: "Billing Schedule",
      key: "billing_schedule",
      type: "select",
      selectOptions: billingScheduleSelectOptions,
      validators: [{ validate: "required" }],
    },
    {
      label: "Check-in Date",
      key: "check_in_date",
      type: "date",
      validators: [{ validate: "required" }],
    },
    {
      label: "Check-out Date",
      key: "check_out_date",
      type: "date",
      validators: [{ validate: "required" }],
    },
    {
      label: "Assignment",
      key: "space_id",
      type: "select",
      selectOptions: spaceSelectOptions,
    },
    ...rateFields,
    {
      label: "Mark Paid Offline",
      key: "mark_paid_offline",
      type: "checkbox",
      requireMapping: true,
      validators: [
        {
          validate: "require_with",
          fields: ["offline_payment_type"],
          errorMessage:
            "Mark Paid Offline must be checked when a payment type is selected",
        },
      ],
    },
    {
      label: "Offline Payment Type",
      key: "offline_payment_type",
      type: "select",
      requireMapping: true,
      selectOptions: offlinePaymentTypes,
      description:
        "Cash payments appear in Dockwa accounting reports. External transactions do not — use for payments already recorded in another system.",
      validators: [
        {
          validate: "require_with_values",
          fieldValues: { mark_paid_offline: true },
          errorMessage: "Payment type is required when marking as paid offline",
        },
      ],
    },
    {
      label: "Reservation Notes",
      key: "reservation_notes",
      type: "string",
    },
  ]

  const columnHooks = [
    { fieldName: "name", callback: standardizeFullName },
    { fieldName: "zip", callback: addLeadingZeroesToZip },
  ]

  const rowHooks = [
    downcaseEmail,
    validateCheckInBeforeCheckOut,
    validateMonthlyBillingRequiresMonthlyPricing,
    validateNonZeroDimensionForDimensionalPricing([], "beam"),
    validateNonZeroDimensionForDimensionalPricing([], "length_overall"),
  ]

  return { fields, columnHooks, rowHooks }
}
